import type { PropertyEliseDto } from '@zg-rentals/ts-ad-management-client';
import { Toast } from '@zillow/constellation-10';
import type {
  useAdManagementEliseAiApiHandler_updateAllEliseAiStatuses,
  useAdManagementEliseAiApiHandler_updateEliseAiStatus,
} from '@zg-rentals/ts-ad-management-client-react-query';
import { useAdManagementEliseAiApiHandler_getEliseStatuses } from '@zg-rentals/ts-ad-management-client-react-query';
import { LoadingConstants, stringHelper } from '@zg-rentals/rental-manager-env';
import type { Dispatch, SetStateAction } from 'react';

export const useGetEliseStatuses = () => {
  return useAdManagementEliseAiApiHandler_getEliseStatuses();
};

export const getSharingStatusForProperty = (property: PropertyEliseDto) => {
  return property.isEliseAiToggledOn;
};

export const onPropertyToggle = (
  property: PropertyEliseDto,
  enqueueToast: (t: JSX.Element) => void,
  updateEliseAiStatusMutation: ReturnType<typeof useAdManagementEliseAiApiHandler_updateEliseAiStatus>,
) => {
  const wasSharing = getSharingStatusForProperty(property);
  const action = (wasSharing ? 'de' : '') + 'activated';
  const newStatus = !wasSharing;

  updateEliseAiStatusMutation.mutate(
    {
      getParams: {
        isEliseAiEnabled: newStatus,
        propertyListingAlias: property.listingAlias,
      },
    },
    {
      onSuccess: () => {
        enqueueToast(<Toast tone="success">Data sharing {action} for 1 property.</Toast>);
      },
      onError: () => {
        enqueueToast(<Toast tone="critical">Unable to activate sharing. Please try again.</Toast>);
      },
    },
  );
};

export const handleBulkAction = (
  action: 'activate' | 'deactivate',
  properties: Array<PropertyEliseDto>,
  selectedRows: Set<string>,
  isSearching: boolean,
  enqueueToast: (t: JSX.Element) => void,
  setLoadingBulkChange: Dispatch<SetStateAction<LoadingConstants>>,
  updateAllEliseAiStatusesMutation: ReturnType<typeof useAdManagementEliseAiApiHandler_updateAllEliseAiStatuses>,
  updateEliseAiStatusMutation: ReturnType<typeof useAdManagementEliseAiApiHandler_updateEliseAiStatus>,
) => {
  const selectedProperties = properties.filter((p) => selectedRows.has(p.listingAlias));

  const isAllSelected = selectedProperties.length === properties.length && !isSearching;

  if (isAllSelected) {
    updateAllEliseAiStatusesMutation.mutate(
      {
        getParams: {
          isEliseAiEnabled: action === 'activate',
        },
      },
      {
        onSuccess: () => {
          const propertyText = stringHelper.pluralize(selectedProperties.length, 'property', 'properties');
          enqueueToast(
            <Toast tone="success">
              Data sharing {action}d for {selectedProperties.length} {propertyText}.
            </Toast>,
          );
          setLoadingBulkChange(LoadingConstants.SUCCESS);
        },
        onError: () => {
          enqueueToast(<Toast tone="critical">Unable to update sharing. Please try again.</Toast>);
          setLoadingBulkChange(LoadingConstants.ERROR);
        },
      },
    );
  } else {
    selectedProperties.forEach((property) => {
      const currentStatusEnabled = getSharingStatusForProperty(property);
      const needsChange =
        (action === 'activate' && !currentStatusEnabled) || (action === 'deactivate' && currentStatusEnabled);

      if (needsChange) {
        updateEliseAiStatusMutation.mutate(
          {
            getParams: {
              isEliseAiEnabled: action === 'activate',
              propertyListingAlias: property.listingAlias,
            },
          },
          {
            onSuccess: () => {
              const propertyText = stringHelper.pluralize(selectedProperties.length, 'property', 'properties');
              enqueueToast(
                <Toast tone="success">
                  Data sharing {action}d for {selectedProperties.length} {propertyText}.
                </Toast>,
              );
              setLoadingBulkChange(LoadingConstants.SUCCESS);
            },
            onError: () => {
              enqueueToast(<Toast tone="critical">Unable to update sharing. Please try again.</Toast>);
              setLoadingBulkChange(LoadingConstants.ERROR);
            },
          },
        );
      } else {
        setLoadingBulkChange(LoadingConstants.SUCCESS);
      }
    });
  }
};
